import { CompactUserResponse, UserId, VeryCompactUserResponse } from '@business/entities/account';
import { CompactContactResponse, ContactId } from '@business/entities/contact';
import { CompactDealBoardResponse, CompactDealConfidenceLevelResponse, CompactDealStatusResponse, DealBoardId } from '@business/entities/deal';
import { CompactDealStageResponse, DealStageId } from '@business/entities/deal/DealStageResponse';
import { CompactOrgResponse, OrgExtra, OrgId } from '@business/entities/organization';
import { CustomFieldResponseType } from '../integration';
import { ProposalStatus } from '../proposal';
import { BaseResponse } from '../common';

export enum HasDeal {
  all = 'all',
  yes = 'yes',
  no = 'no',
}

export enum DealStatus {
  inProgress = 'in_progress',
  hot = 'hot',
  warm = 'warm',
  cool = 'cool',
  cold = 'cold',
  won = 'won',
  lost = 'lost',
  deleted = 'deleted',
  reopen = 'reopen',
  open = 'open',
  cancelled = 'cancelled',
  referred = 'referred',
  // Filter-only pseudo-status: shows deals of every status (never stored on a deal).
  all = 'all',
}

export enum DealAlertStatus {
  success = 'success',
  warning = 'warning',
  danger = 'danger',
}

export type DealId = string;

export interface CompactDealResponse extends BaseResponse {
  id: DealId;
  name: string;
  value: number;
  valueDefault: number;
  interval: string;
  status: DealStatus;
  lostExplain: string;
  expectedClosed: number;
  lastActivity: number;
  menuOrder: number;
  boardId: DealBoardId;
  boardName: string | null;
  stageId: DealStageId;
  stageIsFirstOrLast: boolean;
  orgId: OrgId;
  orgName: string;
  // null when the deal has no owner (owner_id = 0 on the backend)
  owner: VeryCompactUserResponse | null;
  ownerGeoJson?: any | null;
  estimatorUserId: UserId | null;
  estimatorUser: VeryCompactUserResponse | null;
  isWarning: boolean;
  alertStatus: DealAlertStatus;
  alertMessage: string;
  extra: OrgExtra;
  stageTitle: string;
  orgCurrency: string;
  noValue: boolean;
  contact?: CompactContactResponse;
  dealAge: string;
  dealAgeToolip: string;
  // Deal creation timestamp (unix) — shown next to the relative deal age on board cards.
  createdAt: number;
  createdBy: VeryCompactUserResponse;
  timeFrameAlertMessage: string;
  proposalsShort: { id: string; title: string; status: ProposalStatus }[];
  confidenceLevel: CompactDealConfidenceLevelResponse | null;
  dealStatus: CompactDealStatusResponse | null;
  isDisqualified: boolean;
  stageIsLast: boolean;
  hasPendingTmTodos: boolean;
}

export enum DealIntervalResponse {
  onetime = 'one time',
  monthly = 'monthly',
  quarterly = 'quarterly',
  yearly = 'yearly',
}

export type DealRequest = Pick<CompactDealResponse, 'name' | 'value' | 'expectedClosed' | 'interval'> & {
  contactId?: ContactId;
  orgId?: OrgId;
  ownerId?: UserId;
  estimatorUserId?: UserId | null;
  boardId?: DealBoardId;
  stageId?: DealStageId;
  customFields?: { [key: string]: CustomFieldResponseType };
};

export interface DealResponse extends CompactDealResponse {
  stage: CompactDealStageResponse;
  lostReason: string;
  lostExplain: string;
  contact: CompactContactResponse;
  organization: CompactOrgResponse;
  board: CompactDealBoardResponse;
  logs: DealLogResponse[];
  nextId: DealId;
  customFields: { [key: string]: CustomFieldResponseType };
  proposals: DealProposalResponse[];
}

export interface DealLogResponse {
  id: string;
  value: string;
  note: string;
  user: CompactUserResponse;
  createdAt: number;
}
export type DealOrderRequest = {
  stageId: DealStageId;
  deals: DealId[];
};

export interface DealProposalResponse {
  id: string;
  title: string;
  orgId: string;
  status: ProposalStatus;
  amount: number;
  createdBy: CompactUserResponse;
  created: number;
  image: string;
  proposalTotals: {
    monthly: number[];
    oneTime: number[];
    quarterly: number[];
    yearly: number[];
    oneTimeTotal: number;
    monthlyTotal: number;
    quarterlyTotal: number;
    yearlyTotal: number;
  };
}

export interface DealOwnerStatRow {
  ownerName: string;
  ownerId: string;
  total: number;
  disqualified: number;
  quota: number | null;
  percentage: number;
  percentageExcludingDisqualified: number;
}

export interface DealWeeklyStatsResponse {
  startDate: string;
  endDate: string;
  stats: DealOwnerStatRow[];
}
